import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface DeserializeResult<T> {
  /** True if URL has any relevant params */
  hasAnyUrlParams: boolean;
  /** Full state with defaults. Must always be provided so that if sessionStorage is empty, we can initialize it. */
  state: T;
}

export interface UseSyncedStateOptions<T> {
  /** Unique key for sessionStorage */
  sessionStorageKey: string;

  /**
   * Convert state to URL params (only include non-default values).
   * Should be defined outside component or wrapped in useCallback.
   */
  serialize: (state: T) => URLSearchParams;

  /**
   * Parse URL params to full state with defaults.
   * Should be defined outside component or wrapped in useCallback.
   */
  deserialize: (params: URLSearchParams) => DeserializeResult<T>;
}

/**
 * Syncs state between URL params and sessionStorage with priority: URL > sessionStorage > defaults.
 *
 * State is owned by useState — URL and sessionStorage are write-only side effects.
 * This means navigating away (unmount) can't corrupt sessionStorage, because no
 * effects fire on unmount.
 *
 * serialize/deserialize must be inverse operations to avoid sync issues.
 *
 * @example
 * const [filters, updateFilters] = useSyncedState({
 *   sessionStorageKey: "table-filters",
 *   serialize: (state) => {
 *     const params = new URLSearchParams();
 *     if (state.search) params.set("q", state.search);
 *     if (state.page !== 1) params.set("page", state.page.toString());
 *     return params;
 *   },
 *   deserialize: (params) => {
 *     const partial: Partial<TableFilters> = {};
 *     const search = params.get("q");
 *     if (search) partial.search = search;
 *     const page = params.get("page");
 *     if (page) partial.page = parseInt(page);
 *
 *     const defaults = { search: "", page: 1 };
 *     return {
 *       hasAnyUrlParams: Object.keys(partial).length > 0,
 *       state: { ...defaults, ...partial },
 *     };
 *   },
 * });
 */
export function useSyncedState<T>({
  sessionStorageKey,
  serialize,
  deserialize,
}: UseSyncedStateOptions<T>): [
  state: T,
  updateState: (updates: Partial<T>) => void,
] {
  const [, setSearchParams] = useSearchParams();

  // Initialize once: URL > sessionStorage > defaults
  const [state, setState] = useState<T>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlResult = deserialize(params);
    if (urlResult.hasAnyUrlParams) {
      try {
        sessionStorage.setItem(
          sessionStorageKey,
          serialize(urlResult.state).toString(),
        );
      } catch (e) {
        console.error(e);
      }
      return urlResult.state;
    }
    try {
      const raw = sessionStorage.getItem(sessionStorageKey);
      if (raw) {
        // Route sessionStorage through deserialize so values regain their
        // prototypes (e.g. Temporal.PlainDate) and missing fields fall back
        // to defaults — JSON.parse alone would yield plain strings/objects.
        return deserialize(new URLSearchParams(raw)).state;
      }
    } catch (e) {
      console.error(e);
    }
    return urlResult.state;
  });

  // On mount: sync restored state to URL (e.g. sessionStorage → URL params).
  // Mount-only so it can't fire during navigation.
  useEffect(() => {
    setSearchParams(serialize(state), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateState = useCallback(
    (updates: Partial<T>) => {
      const newState = { ...state, ...updates } as T;
      setState(newState);
      const params = serialize(newState);
      setSearchParams(params, { replace: true });
      try {
        sessionStorage.setItem(sessionStorageKey, params.toString());
      } catch (e) {
        console.error(e);
      }
    },
    [state, serialize, setSearchParams, sessionStorageKey],
  );

  return [state, updateState];
}
